import React, { useMemo } from "react";
import styled from "styled-components";
import pick from "lodash/pick";
import { MenuItem, Menu } from "components/bootstrap";
import HasOwnership from "components/common/HasOwnership";
import copyToClipboard from "util/copyToClipboard";
import { Link } from "components/common";
import { INLINE_SEARCH_FILTER_TYPE, REFERENCED_USER_SEARCH_FILTER_TYPE } from "search-filter/constants";
import useSearchFiltersFormActions from "search-filter/hooks/useSearchFiltersFormActions";
import useUserSearchFilterMutation from "search-filter/hooks/useUserSearchFilterMutation";
import useSearchFiltersFormState from "search-filter/hooks/useSearchFiltersFormState";
import CurrentUser from "logic/users/CurrentUser";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import SearchFiltersRoutes from "search-filter/Routes";
import usePermissions from "hooks/usePermissions";
import useScopePermissions from "hooks/useScopePermissions";
const StyledMenu = styled(Menu)`
  white-space: nowrap;
`;
const EditMenuItem = ({
  onClick,
  isReferencedFilter,
  filterId
}) => {
  const { isPermitted } = usePermissions();
  const disabled = isReferencedFilter && !isPermitted([`search_filters:edit:${filterId}`]);
  return /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onClick, icon: "edit", disabled }, "Edit");
};
const useFilterReferenceExplanation = (filterId) => {
  const { isPermitted, isAnyPermitted } = usePermissions();
  if (isPermitted(`search_filters:delete:${filterId}`)) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "This ", /* @__PURE__ */ React.createElement(Link, { to: SearchFiltersRoutes.SHOW(filterId) }, "saved filter"), " is part of your", " ", /* @__PURE__ */ React.createElement(Link, { to: SearchFiltersRoutes.LIST }, '"My Filters"'), " collection.");
  }
  if (isAnyPermitted([`search_filters:edit:${filterId}`, `search_filters:read:${filterId}`])) {
    return "This saved filter has been shared with you.";
  }
  return "You can use this saved filter because it is part of the search.";
};
const ReferenceExplanation = styled.div`
  font-style: italic;
  padding: 0 20px 5px;
  font-size: ${({ theme }) => theme.fonts.size.small};
`;
const ReferencedFilterExplanation = ({ filterId }) => {
  const explanation = useFilterReferenceExplanation(filterId);
  return /* @__PURE__ */ React.createElement(ReferenceExplanation, null, explanation);
};
const transformToInlineFilter = (filter) => {
  const updatedFilter = { ...filter };
  delete updatedFilter.id;
  updatedFilter.type = INLINE_SEARCH_FILTER_TYPE;
  return updatedFilter;
};
const SearchFilterActionsPopover = ({
  filter,
  filter: { id, queryString, frontendId, negation, type },
  onItemClick,
  onEditClick,
  onShareClick,
  isParentMutable = true
}) => {
  const { updateFilters, updateFilter } = useSearchFiltersFormActions();
  const selectedFilters = useSearchFiltersFormState();
  const { post } = useUserSearchFilterMutation();
  const sendTelemetry = useSendTelemetry("search-filter");
  const isReferenced = type === REFERENCED_USER_SEARCH_FILTER_TYPE;
  const { scopePermissions } = useScopePermissions(filter);
  const canEdit = useMemo(() => scopePermissions == null ? void 0 : scopePermissions.is_mutable, [scopePermissions]);
  const canRemoveOrNegate = useMemo(
    () => (scopePermissions == null ? void 0 : scopePermissions.is_mutable) || isParentMutable,
    [scopePermissions, isParentMutable]
  );
  const copyFilterQueryToClipboard = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_COPIED, {
      app_pathname: "search"
    });
    copyToClipboard(queryString);
    onItemClick();
  };
  const removeFilter = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_REMOVED, {
      app_pathname: "search"
    });
    updateFilters(selectedFilters.delete(frontendId));
    onItemClick();
  };
  const saveAsReferenced = () => post({ data: pick(filter, ["queryString", "title", "description"]) }).then((data) => {
    CurrentUser.reload();
    updateFilter(frontendId, {
      ...data,
      negation,
      frontendId: filter.frontendId,
      type: REFERENCED_USER_SEARCH_FILTER_TYPE
    });
    onItemClick();
  });
  const onTransformToInline = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_REFERENCE_REPLACED, {
      app_pathname: "search"
    });
    updateFilter(frontendId, transformToInlineFilter(filter));
    onItemClick();
  };
  const toggleNegation = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_ITEM_NEGATION_TOGGLED, {
      app_pathname: "search",
      event_details: {
        included: !negation
      }
    });
    updateFilter(frontendId, { ...filter, negation: !negation });
    onItemClick();
  };
  return /* @__PURE__ */ React.createElement(StyledMenu, null, canEdit && /* @__PURE__ */ React.createElement(EditMenuItem, { onClick: onEditClick, isReferencedFilter: isReferenced, filterId: id }), canRemoveOrNegate && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { onSelect: toggleNegation, icon: `${negation ? "manage_search" : "search_off"}` }, ` ${negation ? "Include in" : "Exclude from"} results`), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: removeFilter, icon: "delete" }, "Remove from search")), isReferenced && /* @__PURE__ */ React.createElement(HasOwnership, { id, type: "search_filter" }, ({ disabled }) => /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onShareClick, icon: "person_add", disabled }, "Share")), /* @__PURE__ */ React.createElement(MenuItem, { onSelect: copyFilterQueryToClipboard, icon: "content_copy" }, "Copy query to clipboard"), canEdit && isReferenced && /* @__PURE__ */ React.createElement(MenuItem, { onSelect: onTransformToInline, icon: "sync_alt" }, "Replace reference with copy"), canEdit && !isReferenced && /* @__PURE__ */ React.createElement(MenuItem, { onSelect: saveAsReferenced, icon: "save" }, 'Save to "My Filters"'), isReferenced && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(ReferencedFilterExplanation, { filterId: id })));
};
export default SearchFilterActionsPopover;
